import React, { useState } from 'react';
import { useAtomValue as useStateSegmentValue } from 'jotai';
import { RiSearchEyeLine } from '@remixicon/react';
import styles from './NavMenuItem.module.css';
import Tag, { TagVariant } from '../Tag';
import { translate } from '../../../localization';
import Tooltip, { ToolTipSize, TooltipVariant } from '../Tooltip';
import PointingTooltip, { TooltipPosition } from '../PointingTooltip';
import PopoverContainer from '../../molecules/PopoverContainer';
import { MENU_NAMES } from '../../../constants/common';
import { popoverStateSegment } from '../../../store/markettingContentGeneration/stateSegments/popoverStateSegment';
import { PopoverVariant } from '../Popover';
import { QcReviewName } from '../../../types/QualityChecks';

// Props type for NavMenuItem component
interface NavMenuItemProps {
  icon: React.ReactNode;
  text: string;
  name: string;
  tooltip: string;
  isExpanded: boolean;
  isDisabled: boolean;
  handleMenuItemClick: () => void;
  className?: string;
  isActive: boolean;
  isBetaFeature?: boolean;
}

// NavMenuItem component
const NavMenuItem: React.FC<NavMenuItemProps> = ({
  icon,
  text,
  name,
  tooltip,
  isExpanded,
  isDisabled = false,
  handleMenuItemClick,
  className,
  isActive = false,
  isBetaFeature = false,
}) => {
  const [isTooltipVisible, setTooltipVisible] = useState(false);
  const popoverState = useStateSegmentValue(popoverStateSegment);

  const onKeyUpHandler = (event: React.KeyboardEvent<HTMLLIElement>) => {
    if (event.key === 'Enter') {
      handleMenuItemClick();
    }
  };

  const showTooltip = () => setTooltipVisible(true);
  const hideTooltip = () => setTooltipVisible(false);

  const popoverContainerClassname = () => {
    const popoverClasses = [`${styles.popoverContainer}`];

    if (popoverState?.variant === PopoverVariant.WARNING) {
      if (
        popoverState?.buttonLabel &&
        popoverState?.buttonLabel?.trim() !== ''
      ) {
        popoverClasses.push(`${styles.popoverContainerErrorButton}`);
      } else {
        popoverClasses.push(`${styles.popoverContainerError}`);
      }
    }

    return popoverClasses.join(' ').trim();
  };

  return (
    <>
      {isExpanded ? (
        <PopoverContainer
          visible={name === MENU_NAMES.QUALITY_VALIDATION && !isDisabled}
          className={popoverContainerClassname()}
          customIcon={
            popoverState?.qualityChecksStatus === QcReviewName.NEEDS_REVIEW ? (
              <RiSearchEyeLine color='#FF910A' size={24} />
            ) : null
          }
        >
          <li
            className={`${styles['nav-menu-item']} ${
              styles.expanded
            } ${className} ${isActive ? styles.active : ''}`}
            role='menuitem'
            tabIndex={0}
            onClick={handleMenuItemClick}
            aria-expanded={isExpanded}
            onKeyUp={onKeyUpHandler}
          >
            {icon}
            <span className={styles.text}>{text}</span>
            {isBetaFeature && (
              <PointingTooltip
                text={translate('beta_tooltip')}
                showTooltip={isTooltipVisible && isBetaFeature}
                onMouseEnter={showTooltip}
                toolTipPosition={TooltipPosition.RIGHT}
                onMouseLeave={hideTooltip}
                id='beta-tag-tooltip'
                classes={styles.betaTooltipLarge}
              >
                <Tag
                  text={translate('beta')}
                  variant={TagVariant.NEUTRAL}
                  icon={null}
                  classes={styles.betaTagText}
                />
              </PointingTooltip>
            )}
          </li>
        </PopoverContainer>
      ) : (
        <PopoverContainer
          visible={name === MENU_NAMES.QUALITY_VALIDATION && !isDisabled}
          className={popoverContainerClassname()}
          customIcon={
            popoverState?.qualityChecksStatus === QcReviewName.NEEDS_REVIEW ? (
              <RiSearchEyeLine color='#FF910A' size={24} />
            ) : null
          }
        >
          <div onMouseEnter={showTooltip} onMouseLeave={hideTooltip}>
            {/* BASIC SYSTEM TOOLTIP EXAMPLE */}
            {/* TODO: Create a new Tooltip variant using the tooltip implementation used below,
          so that it can be used in multiple places */}
            <Tooltip
              text={tooltip}
              showTooltip={isTooltipVisible}
              onClickedOutside={() => setTooltipVisible(false)}
              variant={TooltipVariant.TOP_RIGHT}
              size={ToolTipSize.REGULAR}
              theme='default'
              id='left-nav-tooltip'
              classes={styles.leftNavTooltip}
            >
              <li
                className={`${styles['nav-menu-item']} ${className} ${
                  isActive ? styles.active : ''
                }`}
                role='menuitem'
                tabIndex={0}
                onClick={handleMenuItemClick}
                aria-expanded={isExpanded}
                onKeyUp={onKeyUpHandler}
              >
                {icon}
              </li>
            </Tooltip>
          </div>
        </PopoverContainer>
      )}
    </>
  );
};

export default NavMenuItem;
